import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  Chip,
  Avatar,
  Rating,
  Button,
  DialogActions,
  DialogTitle,
  Dialog,
  DialogContent,
  FormControl,
  Drawer,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import {
  apiCallVenueReviews,
  // apiCallVenueReviewsAdd,
  apiCallVenueReviewsUpdate,
  apiCallVenueReviewsDelete,
  apiCallVenueReviewsStatus,
} from "../../../../Core/Apicall";
import { useSelector } from "react-redux";
import { RootState } from "../../../../Redux/Store";
import { UploadImages } from "../../../../Core/UploadImages";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { IconButton, Tooltip } from "@mui/material";
import EmptyData from "../../../Common/EmptyData/EmptyData";
import CommonPagination from "../../../Common/CommonPagination/CommonPagination";
import CommonShimmer from "../../../Common/CommonShimmer/CommonShimmer";

export type VenueReview = {
  _id: string;
  venueId: string;
  reviewBy: string;
  rating: number;
  title: string;
  description: string;
  reviewPhotos: string[];
  aiGeneratedReviews: string[];
  fullName?: string;
  userAvatar?: string;
};

const VenueReviews: React.FC<{ venueId: string }> = ({ venueId }) => {
  const [reviews, setReviews] = useState<VenueReview[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);

  const [search, setSearch] = useState<string>("");

  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  const fetchReviews = async () => {
    try {
      setLoading(true);
      setError("");
      const payload = {
        venueId,
        offset: page,
        limit: rowsPerPage,
        search,
      } as any;
      const res = await apiCallVenueReviews(payload);

      if (res?.data) {
        setReviews(res.data);
        setTotalCount(res.totalCount ?? res.data.length ?? 0);
      } else {
        setReviews([]);
        setTotalCount(0);
      }
    } catch (e: any) {
      setError(e?.message || "Failed to load reviews");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, [venueId, page, rowsPerPage, search]);

  const [formData, setFormData] = useState<any>({
    reviewId: "",
    rating: 0,
    title: "",
    description: "",
    reviewPhotos: [],
    aiGeneratedReviews: [],
  });

  const [aiInput, setAiInput] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);
  const handleClose = () => setOpen(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [reviewId, setReviewId] = useState<any>();
  const [selectedMedia, setSelectedMedia] = useState<{
    type: "Image" | "Video" | "Audio";
    url: string;
  } | null>(null);
  const userdata: any = useSelector((state: RootState) => state.global.token);
  const onSubmit = async () => {
    try {
      if (formData?.reviewId) {
        const data = {
          venueId,
          reviewId: formData.reviewId,
          rating: formData.rating,
          title: formData.title,
          description: formData.description,
          reviewPhotos: formData.reviewPhotos,
          aiGeneratedReviews: formData.aiGeneratedReviews,
          userId: userdata?.data?.userId,
        };
        await apiCallVenueReviewsUpdate(data);
      } else {
        const data = {
          venueId,
          rating: formData.rating,
          title: formData.title,
          description: formData.description,
          reviewPhotos: formData.reviewPhotos,
          aiGeneratedReviews: formData.aiGeneratedReviews,
          userId: userdata?.data?.userId,
        };
      }
      fetchReviews();
      setOpen(false);
    } catch (error) {}
  };
  const handleEditReview = (rev: any) => {
    setOpen(true);
    setFormData({
      reviewId: rev._id,
      rating: rev.rating,
      title: rev.title,
      description: rev.description,
      reviewPhotos: rev.reviewPhotos || [],
      aiGeneratedReviews: rev.aiGeneratedReviews || [],
      userId: rev.userId,
    });
  };

  const handleDeleteReview = async (reviewId: any) => {
    try {
      const data = {
        reviewId: reviewId,
        isDelete: true,
      };
      await apiCallVenueReviewsDelete(data);
      setReviewId(null);
      setOpenDelete(false);
      fetchReviews();
    } catch (error) {}
  };

  const toggleReviewStatus = async (reviewId: string, newStatus: boolean) => {
    try {
      const data = {
        reviewId,
        isActive: newStatus,
      };
      await apiCallVenueReviewsStatus(data);
      fetchReviews();
    } catch (error) {}
  };
  const handlePageChange = (value: any) => {
    setPage(value);
  };
  return (
    <Paper sx={{ p: 4, pt: 0, pb: 0, mt: 2 }}>
      <Stack spacing={0}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Review</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell>Photos</TableCell>
                {/* <TableCell>AI Suggestions</TableCell> */}
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
            </TableHead>
            {loading === false && (
              <TableBody>
                {reviews.map((rev: any) => (
                  <TableRow key={rev._id} hover>
                    <TableCell>
                      <Stack alignItems="start" spacing={1}>
                        <Avatar
                          src={
                            rev.profileImagePath
                              ? rev.profileImagePath
                              : rev.userAvatar
                          }
                          sx={{ width: 40, height: 40, cursor: "pointer" }}
                          onClick={() =>
                            setSelectedMedia({
                              type: "Image",
                              url: rev.profileImagePath || rev.userAvatar,
                            })
                          }
                        />
                        <Typography variant="body2">{rev.fullName}</Typography>
                      </Stack>
                      <Dialog
                        open={!!selectedMedia}
                        onClose={() => setSelectedMedia(null)}
                      >
                        {selectedMedia?.type === "Image" && (
                          <Box display="flex" justifyContent="center">
                            <Avatar
                              src={selectedMedia.url}
                              variant="rounded"
                              sx={{ width: 300, height: "auto" }}
                            />
                          </Box>
                        )}
                      </Dialog>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Typography variant="body2" noWrap>
                        {rev.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Rating
                        value={Number(rev.rating) || 0}
                        precision={0.5}
                        readOnly
                        size="small"
                      />
                    </TableCell>

                    <TableCell>
                      {rev.reviewPhotos?.length > 0 && (
                        <>
                          <Box display="flex" alignItems="center">
                            {rev.reviewPhotos
                              .slice(0, 3)
                              .map((photo: string, idx: number) => (
                                <Box
                                  key={idx}
                                  component="img"
                                  src={photo}
                                  alt="review"
                                  onClick={() =>
                                    setSelectedMedia({
                                      type: "Image",
                                      url: photo,
                                    })
                                  } // 👈 open dialog
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    objectFit: "cover",
                                    borderRadius: "50%",
                                    border: "2px solid white",
                                    ml: idx === 0 ? 0 : -2,
                                    boxShadow: 1,
                                    cursor: "pointer",
                                  }}
                                />
                              ))}
                            {rev.reviewPhotos.length > 2 && (
                              <Box
                                onClick={() =>
                                  setSelectedMedia({
                                    type: "Image",
                                    url: rev.reviewPhotos[3],
                                  })
                                }
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: "50%",
                                  bgcolor: "#eee",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: 12,
                                  fontWeight: 500,
                                  ml: -2,
                                  boxShadow: 1,
                                  cursor: "pointer",
                                }}
                              >
                                +{rev.reviewPhotos.length - 2}
                              </Box>
                            )}
                          </Box>
                          <Dialog
                            open={!!selectedMedia}
                            onClose={() => setSelectedMedia(null)}
                          >
                            {selectedMedia?.type === "Image" ? (
                              <Avatar
                                src={selectedMedia.url}
                                variant="rounded"
                                sx={{ width: 300, height: "auto" }}
                              />
                            ) : selectedMedia?.type === "Video" ? (
                              <video width="300" controls autoPlay>
                                <source
                                  src={selectedMedia.url}
                                  type="video/mp4"
                                />
                              </video>
                            ) : selectedMedia?.type === "Audio" ? (
                              <audio
                                controls
                                style={{ width: "300px" }}
                                autoPlay
                              >
                                <source
                                  src={selectedMedia.url}
                                  type="audio/mpeg"
                                />
                              </audio>
                            ) : null}
                          </Dialog>
                        </>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {rev.description}
                      </Typography>
                    </TableCell>
                    {/* <TableCell>
                      {rev.aiGeneratedReviews?.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          {rev.aiGeneratedReviews.join(", ")}
                        </Typography>
                      )}
                    </TableCell> */}
                    <TableCell>
                      <Button
                        size="small"
                        variant={rev.isActive ? "contained" : "outlined"}
                        color={rev.isActive ? "success" : "warning"}
                        onClick={() =>
                          toggleReviewStatus(rev._id, !rev.isActive)
                        }
                        sx={{
                          borderRadius: "12px",
                          textTransform: "capitalize",
                          width: "120px",
                        }}
                      >
                        {rev.isActive ? "Active" : "Inactive"}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit Review">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditReview(rev)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Delete Review">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setOpenDelete(rev._id);
                              setReviewId(rev._id);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      <Dialog
                        open={openDelete}
                        onClose={() => {
                          setReviewId(null);
                          setOpenDelete(false);
                        }}
                      >
                        <DialogTitle>Delete Review</DialogTitle>
                        <DialogContent>
                          Are you sure you want to delete this review?
                        </DialogContent>
                        <DialogActions>
                          <Button
                            onClick={() => {
                              setReviewId(null);
                              setOpenDelete(false);
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            color="error"
                            variant="contained"
                            onClick={() => handleDeleteReview(reviewId)}
                          >
                            Delete
                          </Button>
                        </DialogActions>
                      </Dialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Stack>
      {loading === true && (
        <div style={{ padding: "10px" }}>
          <CommonShimmer />
        </div>
      )}
      <EmptyData data={reviews} />

      <CommonPagination
        page={page}
        rowsPerPage={rowsPerPage}
        totalItems={totalCount}
        totalPages={totalPages}
        onPageChange={(value) => handlePageChange(value)}
        onRowsPerPageChange={(value) => {
          setRowsPerPage(value);
          setPage(1);
        }}
      />

      <Drawer
        anchor={"right"}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: "30%",
          },
        }}
        open={open}
      >
        <DialogTitle> {formData.reviewId ? "Edit" : "Add"} Review</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Rating
              value={formData.rating}
              onChange={(_, newValue) =>
                setFormData((prev: any) => ({ ...prev, rating: newValue }))
              }
            />
          </Box>
          <TextField
            fullWidth
            label="Review"
            multiline
            rows={3}
            sx={{ mt: 2 }}
            value={formData.title}
            onChange={(e) =>
              setFormData((prev: any) => ({
                ...prev,
                title: e.target.value,
              }))
            }
          />

          <TextField
            fullWidth
            label="Description"
            multiline
            rows={3}
            sx={{ mt: 2 }}
            value={formData.description}
            onChange={(e) =>
              setFormData((prev: any) => ({
                ...prev,
                description: e.target.value,
              }))
            }
          />
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth className="mt-2 mb-2">
              <Typography variant="subtitle2" style={{ color: "black" }}>
                Upload Photos <span style={{ color: "red" }}>*</span>
              </Typography>

              <Box
                sx={{ cursor: "pointer" }}
                className="p-5 border-10"
                border="1px solid #0000002A"
                marginTop={2}
                position="relative"
                component="label"
              >
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  multiple
                  onChange={async (e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length === 0) return;

                    const uploadedUrls: string[] = [];

                    for (const file of files) {
                      try {
                        const res = await UploadImages(
                          file,
                          "venue",
                          userdata?.data?.userId
                        );
                        if (res?.location) {
                          uploadedUrls.push(res.location);
                        }
                      } catch (err) {}
                    }
                    setFormData((prev: any) => ({
                      ...prev,
                      reviewPhotos: [...prev.reviewPhotos, ...uploadedUrls],
                    }));
                  }}
                />

                <Box
                  className="d-flex flex-column justify-content-center align-items-center"
                  position="relative"
                >
                  {formData.reviewPhotos.length > 0 ? (
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                      {formData.reviewPhotos.map((url: any, idx: any) => (
                        <Box key={idx} sx={{ position: "relative" }}>
                          <img
                            src={url}
                            alt={`uploaded-${idx}`}
                            width="100"
                            height="100"
                            style={{ borderRadius: 8, objectFit: "cover" }}
                          />
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            sx={{
                              position: "absolute",
                              top: -10,
                              right: -10,
                              minWidth: 28,
                              height: 28,
                              borderRadius: "50%",
                              padding: 0,
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              setFormData((prev: any) => ({
                                ...prev,
                                reviewPhotos: prev.reviewPhotos.filter(
                                  (_: any, i: any) => i !== idx
                                ),
                              }));
                            }}
                          >
                            ✕
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Click to upload photos
                    </Typography>
                  )}
                </Box>
              </Box>
            </FormControl>
          </Box>
          {/* <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="AI Comment"
              value={aiInput}
              onChange={(e) => setAiInput(e.target.value)}
            />
            <Button
              sx={{ mt: 3 }}
              variant="outlined"
              onClick={() => {
                if (aiInput) {
                  setFormData((prev: any) => ({
                    ...prev,
                    aiGeneratedReviews: [...prev.aiGeneratedReviews, aiInput],
                  }));
                  setAiInput("");
                }
              }}
            >
              Add AI Comment
            </Button>
            <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
              {formData.aiGeneratedReviews.map((txt: any, idx: any) => (
                <Chip
                  key={idx}
                  label={txt}
                  onDelete={() =>
                    setFormData((prev: any) => ({
                      ...prev,
                      aiGeneratedReviews: prev.aiGeneratedReviews.filter(
                        (_: any, i: any) => i !== idx
                      ),
                    }))
                  }
                />
              ))}
            </Box>
          </Box> */}
        </DialogContent>

        <DialogActions>
          <Button style={{ color: "black" }} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            style={{ backgroundColor: "black", color: "white" }}
            variant="contained"
            onClick={onSubmit}
          >
            Submit
          </Button>
        </DialogActions>
      </Drawer>
    </Paper>
  );
};

export default VenueReviews;
